import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { AnalyticsHistoryWindow } from "@/api/hip3";
import { ChartCard, ChartSkeleton } from "@/components/v2/ChartCard";
import {
  DenseMarketFiltersDesktop,
  DenseMarketFiltersMobile,
  type DenseMarketStatus,
} from "@/components/v2/DenseMarketFilters";
import { DenseMarketTable } from "@/components/v2/DenseMarketTable";
import { EmptyState, ErrorState } from "@/components/v2/EmptyState";
import { InlineNativeSelect } from "@/components/v2/InlineNativeSelect";
import { LeaderboardTable } from "@/components/v2/LeaderboardTable";
import { OiCategoryTreemap } from "@/components/v2/OiCategoryTreemap";
import { ProviderTable, type ProviderTableRow } from "@/components/v2/ProviderTable";
import { SectionBlock } from "@/components/v2/SectionBlock";
import { StatGridCard } from "@/components/v2/StatGridCard";
import { LineMetricChart, StackedProviderAreaChart } from "@/components/v2/charts";
import {
  useEcosystemHistory,
  useEcosystemOverview,
  useHighlights,
  useMarketLeaderboard,
  useProviderHistory,
  useProviderSummary,
} from "@/hooks/useHip3Analytics";
import { useNormalizedMarkets } from "@/hooks/useNormalizedMarkets";
import {
  aggregateByProvider,
  leaderboardValueHeader,
  normalizeLeaderboardRows,
  shapeProviderDominanceSeries,
} from "@/lib/analytics-transforms";
import { isAnalyticsUnavailable } from "@/lib/errors";
import { formatCompact, formatFundingBps, formatNumber, formatPercent } from "@/lib/formatters";
import { groupMarketsByAsset } from "@/lib/market-grouping";
import { marketNotionalOi } from "@/lib/market-utils";
import {
  buildOiCategoryTreemap,
  parseOiTreemapPathParam,
  stringifyOiTreemapPathParam,
} from "@/lib/oi-treemap";
import {
  normalizeOverviewInterval,
  overviewIntervalOptions,
  sanitizeOverviewHistoryParams,
} from "@/lib/overview-query-state";

function asStatus(raw: string | null): DenseMarketStatus {
  if (raw === "all" || raw === "active" || raw === "inactive") return raw;
  return "active";
}

function hasFiniteChartPoint(points: Array<{ t: string; value: number | null }>): boolean {
  return points.some((point) => typeof point.value === "number" && Number.isFinite(point.value));
}

function normalizeHistoryChartPoints(
  points: Array<{ t?: string; value?: number | null }>
): Array<{ t: string; value: number | null }> {
  return points.reduce<Array<{ t: string; value: number | null }>>((acc, point) => {
    if (typeof point.t !== "string" || !point.t) return acc;

    const value =
      typeof point.value === "number" && Number.isFinite(point.value) ? point.value : null;
    acc.push({ t: point.t, value });
    return acc;
  }, []);
}

function sortMarkets(markets: ReturnType<typeof useNormalizedMarkets>["markets"], sort: string) {
  const cloned = [...markets];
  if (sort === "oi") return cloned.sort((a, b) => marketNotionalOi(b) - marketNotionalOi(a));
  if (sort === "spread") return cloned.sort((a, b) => (a.spreadBps ?? Infinity) - (b.spreadBps ?? Infinity));
  if (sort === "funding") return cloned.sort((a, b) => Math.abs(b.funding ?? 0) - Math.abs(a.funding ?? 0));
  if (sort === "score") return cloned.sort((a, b) => (b.marketScore ?? 0) - (a.marketScore ?? 0));
  return cloned.sort((a, b) => (b.volume24h ?? 0) - (a.volume24h ?? 0));
}

export function OverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawWindow = searchParams.get("window");
  const rawInterval = searchParams.get("interval");
  const sanitizedHistory = sanitizeOverviewHistoryParams(rawWindow, rawInterval);
  const historyWindow: AnalyticsHistoryWindow = sanitizedHistory.window;
  const interval = sanitizedHistory.interval;

  const filters = {
    query: searchParams.get("q") ?? "",
    status: asStatus(searchParams.get("status")),
    sort: searchParams.get("sort") ?? "volume",
    categories: searchParams.getAll("category"),
    providers: searchParams.getAll("provider"),
  };
  const oiTreemapPath = useMemo(
    () => parseOiTreemapPathParam(searchParams.get("oiPath")),
    [searchParams]
  );

  const setParam = (updates: Record<string, string | string[] | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      next.delete(key);
      if (Array.isArray(value)) {
        value.filter(Boolean).forEach((entry) => next.append(key, entry));
      } else if (value) {
        next.set(key, value);
      }
    });
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!sanitizedHistory.changed) return;

    const next = new URLSearchParams(searchParams);
    next.set("window", historyWindow);
    next.set("interval", interval);
    setSearchParams(next, { replace: true });
  }, [historyWindow, interval, sanitizedHistory.changed, searchParams, setSearchParams]);

  const setExplorerFilters = (next: typeof filters) => {
    setParam({
      q: next.query || null,
      status: next.status === "active" ? null : next.status,
      sort: next.sort === "volume" ? null : next.sort,
      category: next.categories,
      provider: next.providers,
    });
  };
  const setOiTreemapPath = (path: string[]) => {
    const serialized =
      path.length > 0 ? stringifyOiTreemapPathParam(path) : null;
    const current = searchParams.get("oiPath");
    if ((current ?? null) === serialized) return;
    setParam({ oiPath: serialized });
  };

  const overviewQuery = useEcosystemOverview({ window: "7d" });
  const highlightsQuery = useHighlights({ window: "24h" });

  const oiQuery = useEcosystemHistory({ metric: "oi", window: historyWindow, interval });
  const volumeQuery = useEcosystemHistory({ metric: "snapshotVolume24h", window: historyWindow, interval });
  const marketCountQuery = useEcosystemHistory({ metric: "marketCount", window: historyWindow, interval });

  const providerSummaryQuery = useProviderSummary({ window: "7d", sort: "oiShare" });
  const providerHistoryQuery = useProviderHistory({ metric: "oiShare", window: historyWindow, interval });

  const oiGrowthLb = useMarketLeaderboard({ metric: "oiGrowth", window: "7d", limit: 5 });
  const spreadLb = useMarketLeaderboard({ metric: "spreadTightest", window: "24h", limit: 5 });
  const fundingVolLb = useMarketLeaderboard({ metric: "fundingVolatility", window: "24h", limit: 5 });

  const marketsQuery = useNormalizedMarkets();

  const oiPoints = useMemo(
    () => normalizeHistoryChartPoints(oiQuery.data?.points ?? []),
    [oiQuery.data?.points]
  );
  const volumePoints = useMemo(
    () => normalizeHistoryChartPoints(volumeQuery.data?.points ?? []),
    [volumeQuery.data?.points]
  );
  const marketCountPoints = useMemo(
    () => normalizeHistoryChartPoints(marketCountQuery.data?.points ?? []),
    [marketCountQuery.data?.points]
  );
  const turnoverPoints = useMemo(() => {
    const oiByTime = new Map(oiPoints.map((point) => [point.t, point.value]));

    return volumePoints.map((point) => {
      const oi = oiByTime.get(point.t);
      if (
        typeof point.value === "number" &&
        Number.isFinite(point.value) &&
        typeof oi === "number" &&
        Number.isFinite(oi) &&
        oi > 0
      ) {
        return {
          t: point.t,
          value: point.value / oi,
        };
      }

      return {
        t: point.t,
        value: null,
      };
    });
  }, [oiPoints, volumePoints]);

  const availableCategories = useMemo(
    () => Array.from(new Set(marketsQuery.markets.map((market) => market.category ?? "General"))).sort(),
    [marketsQuery.markets]
  );

  const availableProviders = useMemo(
    () => Array.from(new Set(marketsQuery.markets.map((market) => market.provider))).sort(),
    [marketsQuery.markets]
  );

  const filteredMarkets = useMemo(() => {
    const searchTerm = filters.query.toLowerCase().trim();

    return sortMarkets(marketsQuery.markets, filters.sort).filter((market) => {
      if (filters.status === "active" && !market.isActive) return false;
      if (filters.status === "inactive" && market.isActive) return false;
      if (filters.categories.length && !filters.categories.includes(market.category ?? "General")) {
        return false;
      }
      if (filters.providers.length && !filters.providers.includes(market.provider)) return false;
      if (!searchTerm) return true;

      const haystack = `${market.baseAsset} ${market.quoteAsset} ${market.marketId} ${market.provider}`.toLowerCase();
      return haystack.includes(searchTerm);
    });
  }, [marketsQuery.markets, filters]);

  const groupedMarkets = useMemo(
    () => groupMarketsByAsset(filteredMarkets, (rows) => sortMarkets(rows, filters.sort)),
    [filteredMarkets, filters.sort]
  );
  const oiTreemapRoot = useMemo(
    () =>
      buildOiCategoryTreemap(marketsQuery.markets, {
        groupSmallNodes: true,
        minPercentageThreshold: 0.015,
        minAbsoluteValueUsd: 5_000,
      }),
    [marketsQuery.markets]
  );

  const providerDominanceData = useMemo(
    () => shapeProviderDominanceSeries(providerHistoryQuery.data?.series ?? []),
    [providerHistoryQuery.data?.series]
  );

  const providerKeys = useMemo(
    () => providerHistoryQuery.data?.series.map((series) => series.provider) ?? [],
    [providerHistoryQuery.data?.series]
  );

  const fallbackProviders = useMemo(
    () => aggregateByProvider(marketsQuery.markets).sort((a, b) => b.oiUsd - a.oiUsd),
    [marketsQuery.markets]
  );

  const providerRows: ProviderTableRow[] = providerSummaryQuery.data?.providers?.length
    ? providerSummaryQuery.data.providers.map((provider) => ({
      provider: provider.provider,
      markets: provider.markets,
      oiShare: provider.oiShare,
      volumeShare: provider.volumeShare,
      oiChangePct: provider.oiChangePct,
      spreadAvgBps: provider.spreadAvgBps,
      fundingMean: provider.fundingMean,
    }))
    : fallbackProviders.map((provider) => ({
      provider: provider.provider,
      markets: provider.markets,
      oiShare: null,
      volumeShare: null,
      oiChangePct: null,
      spreadAvgBps: provider.spreadAvgBps,
      fundingMean: provider.fundingAvg,
    }));

  const highlightItems = highlightsQuery.data?.highlights;

  const oiRows = oiGrowthLb.data ? normalizeLeaderboardRows(oiGrowthLb.data).slice(0, 5) : [];
  const spreadRows = spreadLb.data ? normalizeLeaderboardRows(spreadLb.data).slice(0, 5) : [];
  const fundingRows = fundingVolLb.data ? normalizeLeaderboardRows(fundingVolLb.data).slice(0, 5) : [];

  const hasAnalytics503 = [overviewQuery.error, oiQuery.error, providerSummaryQuery.error].some((error) =>
    isAnalyticsUnavailable(error)
  );

  return (
    <div className="space-y-6">
      <SectionBlock
        title="HIP-3 Ecosystem Overview"
        subtitle="Understand the ecosystem in seconds, then drill down into markets, assets, and providers."
      >
        {hasAnalytics503 ? (
          <ErrorState
            title="Historical analytics unavailable"
            description="The backend persistence layer is disabled. Live market data is still available below."
          />
        ) : null}

        {overviewQuery.isLoading ? (
          <div className="grid gap-2 md:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`stat-skeleton-${index}`} className="h-[108px] animate-pulse border border-border/70 bg-card/60" />
            ))}
          </div>
        ) : overviewQuery.data ? (
          <div className="grid gap-2 md:grid-cols-3">
            <StatGridCard label="Total Markets" value={formatNumber(overviewQuery.data.totals.markets)} sub="Currently tracked" />
            <StatGridCard label="Total Providers" value={formatNumber(overviewQuery.data.totals.providers)} sub="HIP-3 providers" />
            <StatGridCard label="Total Assets" value={formatNumber(overviewQuery.data.totals.assets)} sub="Unique base assets" />
            <StatGridCard label="Total Open Interest" value={formatCompact(overviewQuery.data.totals.currentOi, "$")} delta={overviewQuery.data.totals.oiChangePct} />
            <StatGridCard label="24h Snapshot Volume" value={formatCompact(overviewQuery.data.totals.currentSnapshotVolume24h, "$")} delta={overviewQuery.data.totals.snapshotVolume24hChangePct} />
            <StatGridCard label="7d OI Change" value={formatPercent(overviewQuery.data.totals.oiChangePct)} delta={overviewQuery.data.totals.oiChangePct} />
          </div>
        ) : (
          <ErrorState title="Unable to load ecosystem overview" description="Try refreshing in a few seconds." />
        )}
      </SectionBlock>

      <SectionBlock
        title="Ecosystem Growth"
        subtitle="Macro view of open interest, trading activity, and market count growth."
        actions={
          <div className="flex items-center gap-1.5">
            <InlineNativeSelect
              value={historyWindow}
              onChange={(event) =>
                setParam({
                  window: event.target.value,
                  interval: normalizeOverviewInterval(
                    event.target.value as AnalyticsHistoryWindow,
                    interval
                  ),
                })
              }
              className="h-8 border border-border/70 bg-card pl-2 text-xs font-mono"
            >
              <option value="1d">1d</option>
              <option value="7d">7d</option>
              <option value="30d">30d</option>
            </InlineNativeSelect>
            <InlineNativeSelect
              value={interval}
              onChange={(event) => setParam({ interval: event.target.value })}
              className="h-8 border border-border/70 bg-card pl-2 text-xs font-mono"
            >
              {overviewIntervalOptions(historyWindow).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </InlineNativeSelect>
          </div>
        }
      >
        <div className="grid gap-3 md:grid-cols-2">
          <ChartCard title="Total Open Interest Over Time" loading={oiQuery.isLoading}>
            {oiQuery.error ? (
              <ChartMessage title="Unable to load OI history" description="Check analytics availability or refresh." />
            ) : hasFiniteChartPoint(oiPoints) ? (
              <LineMetricChart data={oiPoints} valueFormatter={(value) => formatCompact(value, "$")} />
            ) : (
              <ChartMessage title="No OI data" description="No valid points for this window and interval." />
            )}
          </ChartCard>
          <ChartCard title="24h Volume Snapshot Over Time" loading={volumeQuery.isLoading}>
            {volumeQuery.error ? (
              <ChartMessage title="Unable to load volume history" description="Check analytics availability or refresh." />
            ) : hasFiniteChartPoint(volumePoints) ? (
              <LineMetricChart data={volumePoints} valueFormatter={(value) => formatCompact(value, "$")} />
            ) : (
              <ChartMessage title="No volume data" description="No valid points for this window and interval." />
            )}
          </ChartCard>
          <ChartCard title="Market Count Growth" loading={marketCountQuery.isLoading}>
            {marketCountQuery.error ? (
              <ChartMessage title="Unable to load market count history" description="Check analytics availability or refresh." />
            ) : hasFiniteChartPoint(marketCountPoints) ? (
              <LineMetricChart data={marketCountPoints} valueFormatter={(value) => formatNumber(value, 0)} />
            ) : (
              <ChartMessage title="No market count data" description="No valid points for this window and interval." />
            )}
          </ChartCard>
          <ChartCard title="24h Turnover (Volume / OI)" loading={oiQuery.isLoading || volumeQuery.isLoading}>
            {oiQuery.error || volumeQuery.error ? (
              <ChartMessage title="Unable to load turnover history" description="Check analytics availability or refresh." />
            ) : hasFiniteChartPoint(turnoverPoints) ? (
              <LineMetricChart
                data={turnoverPoints}
                valueFormatter={(value) => `${formatNumber(value, 2)}x`}
                axisFormatter={(value) => `${formatNumber(value, 2)}x`}
              />
            ) : (
              <ChartMessage title="No turnover data" description="No valid points for this window and interval." />
            )}
          </ChartCard>
        </div>
      </SectionBlock>

      <SectionBlock title="Activity & Signals">
        {highlightsQuery.isLoading ? (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={`highlight-skeleton-${index}`} className="h-[78px] animate-pulse border border-border/70 bg-card/60" />
            ))}
          </div>
        ) : highlightItems ? (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
            <SignalCard title="Biggest OI Growth (24h)" label={highlightItems.topOiGainer?.label ?? "—"} value={formatPercent(highlightItems.topOiGainer?.value)} to={highlightItems.topOiGainer ? `/markets/${highlightItems.topOiGainer.provider}/${highlightItems.topOiGainer.marketId}` : undefined} />
            <SignalCard title="Biggest Volume Growth (24h)" label={highlightItems.topVolumeGainer?.label ?? "—"} value={formatPercent(highlightItems.topVolumeGainer?.value)} to={highlightItems.topVolumeGainer ? `/markets/${highlightItems.topVolumeGainer.provider}/${highlightItems.topVolumeGainer.marketId}` : undefined} />
            <SignalCard title="Tightest Spread (24h)" label={highlightItems.tightestSpread?.label ?? "—"} value={formatNumber(highlightItems.tightestSpread?.value, 2)} suffix="bps" to={highlightItems.tightestSpread ? `/markets/${highlightItems.tightestSpread.provider}/${highlightItems.tightestSpread.marketId}` : undefined} />
            <SignalCard title="Funding Instability (24h)" label={highlightItems.mostUnstableFunding?.label ?? "—"} value={formatFundingBps(highlightItems.mostUnstableFunding?.value)} to={highlightItems.mostUnstableFunding ? `/markets/${highlightItems.mostUnstableFunding.provider}/${highlightItems.mostUnstableFunding.marketId}` : undefined} />
            <SignalCard title="Provider Share Gainer (24h)" label={highlightItems.topProviderShareGainer?.provider?.toUpperCase() ?? "—"} value={formatPercent((highlightItems.topProviderShareGainer?.value ?? 0) * 100)} to={highlightItems.topProviderShareGainer ? `/providers/${highlightItems.topProviderShareGainer.provider}` : undefined} />
          </div>
        ) : (
          <EmptyState title="No highlights available" description="Highlights appear after enough persistence snapshots accumulate." />
        )}

        <div className="grid gap-2 lg:grid-cols-2">
          <LeaderboardTable title="Top OI Growth (7d, Top 5)" rows={oiRows} valueHeader={leaderboardValueHeader("oiGrowth")} />
          <LeaderboardTable title="Tightest Spreads (24h, Top 5)" rows={spreadRows} valueHeader={leaderboardValueHeader("spreadTightest")} />
        </div>
        <div className="mt-2">
          <LeaderboardTable
            title="Funding Instability (24h, Top 5)"
            rows={fundingRows}
            valueHeader={leaderboardValueHeader("fundingVolatility")}
            subtitle="Instability = funding standard deviation in bps over 24h."
          />
        </div>
      </SectionBlock>

      <SectionBlock
        title="OI Distribution"
        subtitle="Current open interest breakdown by category, then asset, then market."
      >
        <ChartCard
          title="OI by Category (Tree)"
          description="Snapshot from current HIP-3 markets. Drill into any block to inspect concentration."
          loading={marketsQuery.isLoading}
        >
          {marketsQuery.error ? (
            <ChartMessage
              title="Unable to load OI treemap"
              description="Check market data availability or refresh."
            />
          ) : oiTreemapRoot.value > 0 && oiTreemapRoot.children?.length ? (
            <OiCategoryTreemap
              root={oiTreemapRoot}
              path={oiTreemapPath}
              onPathChange={setOiTreemapPath}
              maxDepth={3}
            />
          ) : (
            <ChartMessage
              title="No OI distribution available"
              description="No markets with valid notional open interest are available right now."
            />
          )}
        </ChartCard>
      </SectionBlock>

      <SectionBlock title="Provider Dominance" subtitle="Competition for ecosystem share over time and current provider footprint.">
        <ChartCard title="Provider Share of OI" loading={providerHistoryQuery.isLoading}>
          <StackedProviderAreaChart data={providerDominanceData} providers={providerKeys} tooltipVariant="contrast" />
        </ChartCard>
        {providerSummaryQuery.isLoading ? <ChartSkeleton /> : <ProviderTable rows={providerRows} />}
      </SectionBlock>

      <SectionBlock title="All HIP-3 Markets">
        <div className="grid w-full gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <div className="hidden lg:block min-w-0">
            <DenseMarketFiltersDesktop
              filters={filters}
              categories={availableCategories}
              providers={availableProviders}
              sortOptions={[
                { value: "volume", label: "24h Volume" },
                { value: "oi", label: "Open Interest" },
                { value: "spread", label: "Tightest Spread" },
                { value: "funding", label: "Funding Magnitude" },
                { value: "score", label: "Market Score" },
              ]}
              onChange={setExplorerFilters}
              onReset={() =>
                setExplorerFilters({
                  query: "",
                  status: "active",
                  sort: "volume",
                  categories: [],
                  providers: [],
                })
              }
            />
          </div>

          <div className="space-y-2 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[0.7rem] uppercase tracking-wide text-muted-foreground font-mono">
                Showing {filteredMarkets.length} markets across {groupedMarkets.length} assets
              </div>

              <DenseMarketFiltersMobile
                filters={filters}
                categories={availableCategories}
                providers={availableProviders}
                sortOptions={[
                  { value: "volume", label: "24h Volume" },
                  { value: "oi", label: "Open Interest" },
                  { value: "spread", label: "Tightest Spread" },
                  { value: "funding", label: "Funding Magnitude" },
                  { value: "score", label: "Market Score" },
                ]}
                onChange={setExplorerFilters}
                onReset={() =>
                  setExplorerFilters({
                    query: "",
                    status: "active",
                    sort: "volume",
                    categories: [],
                    providers: [],
                  })
                }
              />
            </div>

            <DenseMarketTable groupedMarkets={groupedMarkets} />
          </div>
        </div>
      </SectionBlock>
    </div>
  );
}

function SignalCard({
  title,
  label,
  value,
  suffix,
  to,
}: {
  title: string;
  label: string;
  value: string;
  suffix?: string;
  to?: string;
}) {
  const content = (
    <div className="h-full border border-border/70 bg-card/90 p-2.5 transition hover:border-primary/40">
      <div className="text-[0.62rem] uppercase tracking-wide font-mono text-muted-foreground">{title}</div>
      <div className="mt-1.5 text-sm font-semibold">{label}</div>
      <div className="mt-0.5 text-sm text-primary font-mono">
        {value} {suffix ?? ""}
      </div>
    </div>
  );

  if (!to) return content;
  return <Link to={to}>{content}</Link>;
}

function ChartMessage({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-[260px] w-full items-center justify-center border border-border/70 bg-background/45 px-4 text-center">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
